/**
 * Selling surplus stock off the van.
 *
 * WHY THIS SCREEN EXISTS
 *
 * It was the only part of the feature missing. The endpoint accepted
 * `isExtraBoxSale`, the `order.create_extra_box` permission was seeded, the
 * stock movement type EXTRA_BOX_SALE existed, the reconciliation formula
 * subtracted `extra_box_sold`, and `pod.extraBoxSale` — "Sell extra stock" —
 * was translated into both languages. No screen referenced any of it, so a
 * driver could not record a van sale at all.
 *
 * That is not a missing nicety. This trade runs on it: a shop asks for two
 * more cartons while the driver is at the door. With no way to record it, the
 * goods leave the van and the app still counts them, so load-in shows a
 * shortage — and `variance_flagged` is the field Admin reviews and holds the
 * driver to. The app was set up to blame drivers for sales it would not let
 * them enter.
 *
 * THREE THINGS THIS SCREEN REFUSES TO GUESS
 *
 *   1. WHAT IS ON THE VAN. Quantities come from the load sheet's van stock,
 *      and the stepper is capped at what is actually there. The server checks
 *      again — this cap is a courtesy, not the guard.
 *   2. THE PRICE. Never entered here. The server prices from the customer's
 *      own price list, exactly as for an ordinary order, so a doorstep sale
 *      cannot quietly undercut the rate card.
 *   3. THE BUYER. Picked from shops near the driver's current position, which
 *      is already scoped server-side to this driver's route and history.
 *
 * OFFLINE
 *
 * Deliberately NOT queued to the outbox. An extra-box sale allocates an
 * invoice number and moves van stock, and the server's availability check is
 * the only thing standing between a driver and selling stock that is not
 * there. Queueing it would mean accepting the sale on the device and
 * discovering hours later that it was refused, with the goods already handed
 * over. A POD can be queued because the delivery is a fact either way; this
 * is a decision, and it needs the answer now.
 */

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Page } from '../../components/Shell';
import {
  Banner, Button, Card, Empty, Select, Sheet, Skeleton, Stepper, TextInput, useToast,
} from '../../components/ui';
import { useQuery, useDebounced, useOnline } from '../../lib/useApi';
import { useT } from '../../lib/i18n';
import { api, ApiError } from '../../lib/api';
import { getCoords } from '../../lib/camera';
import { money, piecesAndPacks } from '../../lib/format';

interface VanStock {
  // Shape taken from stock.ts's van-stock handler, not guessed: `status` is a
  // top-level field alongside `loadSheetNumber`, not nested under a load-sheet
  // object.
  loadSheetNumber: string;
  status: string;
  items: {
    productId: string; skuCode: string; productName: string;
    loaded: number; delivered: number; extraBoxSold: number; returnedIn: number;
    expectedResidual: number;
    /** Returned by the same endpoint; this screen used to drop them. */
    packUom: string | null; packQty: number | null;
  }[];
}

interface NearbyShops {
  shops: {
    shopId: string; customerId: string; shopName: string; customerName: string;
    address: string;
    /** Null when the shop has no pinned location — most of them. */
    distanceKm: number | null;
    outstandingPaise: number;
  }[];
}

interface OrderResult {
  orderId: string;
  orderNumber: string;
  invoiceNumber?: string;
  netAmountPaise?: number;
}

const PAYMENT_MODES = ['cash', 'upi_online', 'credit'] as const;

export function ExtraBoxSale() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const online = useOnline();

  const [qty, setQty] = useState<Record<string, number>>({});
  const [shopId, setShopId] = useState('');
  const [search, setSearch] = useState('');
  const [paymentMode, setPaymentMode] = useState<(typeof PAYMENT_MODES)[number]>('cash');
  const [remarks, setRemarks] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [coords, setCoords] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locating, setLocating] = useState(false);
  const [placed, setPlaced] = useState<OrderResult | null>(null);

  const { data: van, loading, error, refetch } = useQuery<VanStock>(
    id ? `/api/stock/load-sheets/${id}/van-stock` : null,
  );

  const debouncedSearch = useDebounced(search, 350);
  const { data: found, loading: searching } = useQuery<NearbyShops>(
    debouncedSearch.trim().length >= 2
      ? `/api/customers/nearby?search=${encodeURIComponent(debouncedSearch.trim())}`
      : null,
  );

  const { data: nearby, loading: findingShops } = useQuery<NearbyShops>(
    coords
      ? `/api/customers/nearby?latitude=${coords.latitude}&longitude=${coords.longitude}&radiusKm=2`
      : null,
    { deps: [coords?.latitude, coords?.longitude] },
  );

  /**
   * SELLABLE, WHICH IS NOT THE SAME AS ON THE VAN.
   *
   * `returnedIn` is stock picked up back from customers — leakage, damage,
   * expiry — and it is on the van without being sellable. `expectedResidual`
   * includes it, correctly, because it is counting what should physically be
   * there. Selling from it would be reselling returns as fresh, so this
   * subtracts it. The server applies the same rule and is the one that counts.
   */
  const sellable = useMemo(
    () => (van?.items ?? [])
      .map((i) => ({ ...i, available: i.loaded - i.delivered - i.extraBoxSold }))
      .filter((i) => i.available > 0),
    [van],
  );

  const lines = useMemo(
    () => Object.entries(qty)
      .filter(([, n]) => n > 0)
      .map(([productId, quantity]) => ({ productId, quantity })),
    [qty],
  );

  /**
   * Search results and nearby results, as one list.
   *
   * Search wins when there is a search term: a driver who has typed a name is
   * answering a more specific question than "what is near me", and merging the
   * two would bury his answer among shops he did not ask about.
   */
  const choices = search.trim().length >= 2
    ? (found?.shops ?? [])
    : (nearby?.shops ?? []);

  const chosenShop = choices.find((s) => s.shopId === shopId);

  const findShops = async () => {
    setLocating(true);
    try {
      const c = await getCoords();
      if (!c) {
        toast.show(
          'Location is needed to list nearby shops. Allow location access and try again.',
          'danger',
        );
        return;
      }
      setCoords({ latitude: c.latitude, longitude: c.longitude });
    } finally {
      setLocating(false);
    }
  };

  const submit = async () => {
    if (!id) return;
    if (!chosenShop) {
      toast.show('Choose the customer buying the stock.', 'danger');
      return;
    }
    if (lines.length === 0) {
      toast.show('Enter how much is being sold.', 'danger');
      return;
    }

    setBusy(true);
    try {
      const result = await api.post<OrderResult>('/api/orders', {
        customerId: chosenShop.customerId,
        customerShopId: chosenShop.shopId,
        lines,
        paymentMode,
        remarks: remarks.trim() || undefined,
        isExtraBoxSale: true,
        // Not optional in practice. Without it the server refuses the sale,
        // because there would be no van for the stock to leave.
        loadSheetId: id,
      });
      setPlaced(result);
      setConfirmOpen(false);
      setQty({});
      setRemarks('');
      await refetch();
    } catch (err) {
      // The server's message names the SKU and how many are actually on the
      // van, so it is shown as-is rather than replaced with "Failed".
      toast.show(
        err instanceof ApiError || err instanceof Error ? err.message : 'Could not record the sale',
        'danger',
      );
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Page title={t('pod.extraBoxSale')}><Skeleton rows={5} /></Page>;
  if (error || !van) {
    return (
      <Page title={t('pod.extraBoxSale')}>
        {/* `error` from useQuery is an ApiError, not a string. Its `.message`
            is the server's own wording, which is worth showing — this screen's
            whole reason for existing is that the server's message names the
            SKU and the quantity actually on the van. */}
        <Empty title="Van stock could not be loaded" hint={error?.message} />
      </Page>
    );
  }

  /**
   * The van has to be out. 'accepted' and 'in_transit' are the two states
   * where the driver holds the goods; anything else and the server refuses,
   * so saying so here saves a round trip and a confusing error.
   */
  const sellingAllowed = van.status === 'accepted' || van.status === 'in_transit';

  if (placed) {
    return (
      <Page title={t('pod.extraBoxSale')} back>
        <div className="stack-4">
          <Banner tone="ok" title="Sale recorded">
            <span className="t-sm">
              {placed.invoiceNumber ?? placed.orderNumber}
              {placed.netAmountPaise !== undefined && ` — ${money(placed.netAmountPaise)}`}
              . Van stock has been reduced.
            </span>
          </Banner>
          <Button block onClick={() => navigate(`/routes/${id}`)}>Back to the route</Button>
          <Button variant="ghost" block onClick={() => setPlaced(null)}>Record another</Button>
        </div>
      </Page>
    );
  }

  return (
    <Page title={t('pod.extraBoxSale')} back>
      <div className="stack-4">
        {!sellingAllowed && (
          <Banner tone="warn" title="Not while the van is here">
            <span className="t-sm">
              This load sheet is “{van.status}”. An extra-box sale can only be
              recorded while the van is out with you.
            </span>
          </Banner>
        )}

        {!online && (
          <Banner tone="warn" title="This one needs a connection">
            <span className="t-sm">
              An extra-box sale issues an invoice number and checks what is left on the van,
              so it cannot be queued for later. Deliveries still can.
            </span>
          </Banner>
        )}

        {/* --- Who is buying ------------------------------------------------ */}
        <Card>
          <div className="card__header"><h3>Customer</h3></div>
          {/*
            SEARCH BY NAME, NOT ONLY BY PROXIMITY.

            Nearby was the only way in, and it fails exactly where this screen
            is used. Most customers have no coordinates at all — the Business
            Central import never captured any — so a driver parked outside a
            shop could be shown "no customers within 2 km" while standing in
            front of one. The sale then either does not happen or is recorded
            against the wrong shop.

            Requested 2 Oct as selling extra stock "with direct customer
            selection". Nearby stays, because when it works it is the fastest
            path and it puts the right shop first.
          */}
          <div className="stack-2">
            <TextInput
              label="Search by name or phone"
              placeholder="Shop name, code or number"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {!coords && !search.trim() && (
              <Button
                variant="ghost" block busy={locating}
                onClick={() => void findShops()}
              >
                Find customers near me
              </Button>
            )}
          </div>

          {(findingShops || searching) && <Skeleton rows={2} />}
          {!findingShops && !searching && choices.length === 0 && (coords || search.trim()) && (
            <Empty
              title={search.trim() ? 'No customer matched' : 'No customers within 2 km'}
              hint={search.trim()
                ? 'Try part of the name, the customer code, or the phone number.'
                : 'Search by name instead — most customers have no location pinned.'}
            />
          )}
          {choices.length > 0 && (
            <Select
              label="Buying customer"
              value={shopId}
              onChange={(e) => setShopId(e.target.value)}
              options={[
                { value: '', label: 'Choose a customer' },
                ...choices.map((s) => ({
                  value: s.shopId,
                  /**
                   * A distance only when there is one.
                   *
                   * `distanceKm.toFixed(1)` on a null threw and took the whole
                   * screen with it. Most shops have no coordinates — the
                   * Business Central import never captured any — so the common
                   * case was the crashing one.
                   */
                  label: s.distanceKm === null
                    ? `${s.shopName} · no location pinned`
                    : `${s.shopName} · ${s.distanceKm.toFixed(1)} km`,
                })),
              ]}
            />
          )}
          {chosenShop && chosenShop.outstandingPaise > 0 && (
            <div className="t-xs t-warn" style={{ marginTop: 6 }}>
              {/* Shown, not blocked. The credit decision belongs to the server's
                  credit-limit check, which this screen does not second-guess. */}
              Outstanding {money(chosenShop.outstandingPaise)}
            </div>
          )}
        </Card>

        {/* --- What is being sold ------------------------------------------- */}
        <Card flush>
          <div className="card__header"><h3>On the van</h3></div>
          {sellable.length === 0 ? (
            <Empty
              title="Nothing left to sell"
              hint="Everything loaded has been delivered or already sold."
            />
          ) : (
            <div className="list">
              {sellable.map((item) => (
                <div key={item.productId} className="list__item list__item--static">
                  <div className="grow" style={{ minWidth: 0 }}>
                    <div className="t-sm t-semi t-clip">{item.productName}</div>
                    <div className="t-xs t-muted">
                      {/*
                        Boxes as well as pieces. A driver selling leftover
                        stock off the van is asked "how many?" by a shopkeeper
                        in cartons, and "37 available" does not answer that.
                      */}
                      {item.skuCode} ·{' '}
                      {item.packQty && item.packQty > 1
                        ? piecesAndPacks(item.available, {
                          baseUom: 'PCS', packUom: item.packUom, packQty: item.packQty,
                        })
                        : `${item.available} available`}
                      {item.returnedIn > 0 && ` · ${item.returnedIn} returned (not sellable)`}
                    </div>
                  </div>
                  <Stepper
                    value={qty[item.productId] ?? 0}
                    min={0}
                    /* Capped at what is on the van. The server checks again —
                       a stepper is not a security boundary. */
                    max={item.available}
                    onChange={(n) => setQty((q) => ({ ...q, [item.productId]: n }))}
                  />
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* --- How it is being paid for ------------------------------------- */}
        <Card>
          <div className="card__header"><h3>Payment</h3></div>
          <Select
            label="Payment mode"
            value={paymentMode}
            onChange={(e) => setPaymentMode(e.target.value as typeof paymentMode)}
            options={[
              { value: 'cash', label: 'Cash' },
              { value: 'upi_online', label: 'UPI' },
              { value: 'credit', label: 'Credit' },
            ]}
          />
          <TextInput
            label="Remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            maxLength={500}
          />
          {/* No price field anywhere on this screen, on purpose. The rate comes
              from the customer's price list server-side. */}
          <div className="t-xs t-muted" style={{ marginTop: 6 }}>
            The rate is taken from this shop’s price list. It is not entered here.
          </div>
        </Card>
      </div>

      <div className="actionbar">
        <Button
          block size="lg"
          disabled={!sellingAllowed || !online || lines.length === 0 || !chosenShop}
          onClick={() => setConfirmOpen(true)}
        >
          {t('app.confirm')}
        </Button>
      </div>

      <Sheet
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Record this sale?"
        footer={
          <>
            <Button variant="ghost" block onClick={() => setConfirmOpen(false)} disabled={busy}>
              {t('app.cancel')}
            </Button>
            <Button block busy={busy} onClick={() => void submit()}>{t('app.confirm')}</Button>
          </>
        }
      >
        <div className="stack-4">
          <Banner tone="warn">
            <span className="t-sm">
              This issues a tax invoice immediately and takes the stock off your van.
              An issued invoice cannot be deleted — it has to be reversed by a credit note.
            </span>
          </Banner>
          <Card flush>
            <div className="list">
              {lines.map((l) => {
                const item = sellable.find((s) => s.productId === l.productId);
                return (
                  <div key={l.productId} className="list__item list__item--static">
                    <div className="grow" style={{ minWidth: 0 }}>
                      <div className="t-sm t-clip">{item?.productName ?? l.productId}</div>
                      {item?.packQty && item.packQty > 1 && (
                        <div className="t-xs t-muted">
                          {piecesAndPacks(l.quantity, {
                            baseUom: 'PCS', packUom: item.packUom, packQty: item.packQty,
                          }).split(' · ')[1]}
                        </div>
                      )}
                    </div>
                    <span className="t-lg t-bold t-mono">{l.quantity}</span>
                  </div>
                );
              })}
            </div>
          </Card>
          <div className="t-sm">
            {chosenShop?.shopName} · {paymentMode === 'upi_online' ? 'UPI' : paymentMode}
          </div>
        </div>
      </Sheet>
    </Page>
  );
}
